/**
 * `rss` — RSS 2.0, Atom, RDF (sources.md "rss"). Input: a feed URL, or a page URL with autodiscovery.
 * Locator: the feed URL, scheme and host lower-cased, default port and fragment removed.
 * Cursor: `{ newestAt }` — informational (dedupe is by external id); conditional requests do the saving.
 */

import type { FetchResponse } from '../contracts/platform.js';
import type { RawItem, SourceClient, SourceFetchContext, SourceFetchResult, SourceRuntime } from '../contracts/sources.js';
import { DigesterError } from '../kernel/errors.js';
import { discoverFeedUrl, entryToItem, looksLikeFeed, parseFeed } from './feed.js';
import { assertOk, canonicalUrl, contextConditionalHeaders, invalidInput, newestFirst, validatorsOf } from './http.js';

export const FEED_ACCEPT = 'application/rss+xml, application/atom+xml, application/xml, text/xml;q=0.9, */*;q=0.8';

async function get(runtime: SourceRuntime, url: string, headers: Record<string, string> = {}): Promise<FetchResponse> {
  return runtime.fetcher.fetch({ url, method: 'GET', headers: { Accept: FEED_ACCEPT, ...headers }, route: 'auto' });
}

/** Parse a feed body into items (shared with tests and the smoke run). */
export function feedItems(xml: string, feedUrl: string): { title: string; items: RawItem[] } {
  const feed = parseFeed(xml, `The feed ${feedUrl}`);
  const items: RawItem[] = [];
  for (const entry of feed.entries) {
    const item = entryToItem(entry, feedUrl);
    if (item) items.push(item);
  }
  return { title: feed.title, items };
}

function hostName(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

export const rssClient: SourceClient<'rss'> = {
  kind: 'rss',
  defaultPullMinutes: 15,

  async resolve(input, runtime) {
    const typed = canonicalUrl(input.config?.feedUrl ?? input.address);
    if (!typed) throw invalidInput('Enter a feed URL or the address of a page that links to one.');
    const now = runtime.clock.now();
    const first = await get(runtime, typed);
    assertOk(first, { credential: false, what: typed, now });
    let feedUrl = typed;
    let body = first;
    if (!looksLikeFeed(first.text, first.headers['content-type'])) {
      const discovered = discoverFeedUrl(first.text, first.url || typed);
      if (!discovered) throw new DigesterError('parse_failed', 'That page does not link to an RSS or Atom feed.');
      feedUrl = canonicalUrl(discovered) ?? discovered;
      body = await get(runtime, feedUrl);
      assertOk(body, { credential: false, what: feedUrl, now });
    }
    const { title } = feedItems(body.text, feedUrl);
    return {
      kind: 'rss',
      locator: feedUrl,
      name: title || hostName(feedUrl),
      config: { kind: 'rss', feedUrl },
      requiredSecrets: [],
    };
  },

  async fetch(ctx: SourceFetchContext<'rss'>): Promise<SourceFetchResult> {
    const { feedUrl } = ctx.source.config;
    const response = await get(ctx.runtime, feedUrl, contextConditionalHeaders(ctx));
    assertOk(response, { credential: false, what: ctx.source.name || feedUrl, now: ctx.runtime.clock.now() });
    const cursor = ctx.cursor ?? {};
    if (response.status === 304) return { items: [], cursor, notModified: true };
    const items = newestFirst(feedItems(response.text, feedUrl).items, ctx.maxItems);
    const newest = items.find((i) => i.publishedAt !== undefined)?.publishedAt;
    return {
      items,
      cursor: newest !== undefined ? { ...cursor, newestAt: Math.max(newest, typeof cursor.newestAt === 'number' ? cursor.newestAt : 0) } : cursor,
      ...validatorsOf(response),
    };
  },
};
